import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ChartBarIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import { useAdminStats } from "../hooks/useAdminStats";
import { MetricsCharts } from "../components/MetricsCharts";
import { getSupabase } from "@/shared/db";
import { Select } from "@/shared/ui";

type Period = "day" | "week" | "month" | "year";

export const MetricsPage: React.FC = () => {
  const { t } = useTranslation();
  const { stats, documentStats, loading, error, refetch } = useAdminStats();
  const [period, setPeriod] = useState<Period>("week");
  const [timeSeriesData, setTimeSeriesData] = useState<
    Array<{ date: string; users: number; documents: number }>
  >([]);
  const [loadingTimeSeries, setLoadingTimeSeries] = useState(false);

  useEffect(() => {
    fetchTimeSeriesData();
  }, [period]);

  const fetchTimeSeriesData = async () => {
    setLoadingTimeSeries(true);
    try {
      const now = new Date();
      const startDate = new Date();

      switch (period) {
        case "day":
          startDate.setDate(now.getDate() - 7); // Last 7 days
          break;
        case "week":
          startDate.setDate(now.getDate() - 30); // Last 30 days
          break;
        case "month":
          startDate.setMonth(now.getMonth() - 6); // Last 6 months
          break;
        case "year":
          startDate.setFullYear(now.getFullYear() - 1); // Last year
          break;
      }

      const supabase = getSupabase();

      // Get users created in period
      const { data: usersData } = await supabase
        .from("profiles")
        .select("created_at")
        .gte("created_at", startDate.toISOString())
        .order("created_at", { ascending: true });

      // Get documents created in period
      const { data: documentsData } = await supabase
        .from("documents")
        .select("created_at")
        .gte("created_at", startDate.toISOString())
        .order("created_at", { ascending: true });

      // Group by date
      const dateMap = new Map<string, { users: number; documents: number }>();

      // Initialize all dates in range
      const currentDate = new Date(startDate);
      while (currentDate <= now) {
        const dateKey = currentDate.toISOString().split("T")[0];
        dateMap.set(dateKey, { users: 0, documents: 0 });
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Count users by date
      usersData?.forEach((user) => {
        const dateKey = user.created_at.split("T")[0];
        const existing = dateMap.get(dateKey) || { users: 0, documents: 0 };
        dateMap.set(dateKey, { ...existing, users: existing.users + 1 });
      });

      // Count documents by date
      documentsData?.forEach((doc) => {
        const dateKey = doc.created_at.split("T")[0];
        const existing = dateMap.get(dateKey) || { users: 0, documents: 0 };
        dateMap.set(dateKey, {
          ...existing,
          documents: existing.documents + 1,
        });
      });

      // Convert to array and format dates
      const timeSeries = Array.from(dateMap.entries())
        .map(([date, counts]) => ({
          date: new Date(date).toLocaleDateString("es-ES", {
            month: "short",
            day: "numeric",
          }),
          users: counts.users,
          documents: counts.documents,
        }))
        .slice(-30); // Show last 30 data points

      setTimeSeriesData(timeSeries);
    } catch (err) {
      console.error("Error fetching time series data:", err);
    } finally {
      setLoadingTimeSeries(false);
    }
  };

  const handleExport = () => {
    // Create CSV content
    const headers = [
      t("admin.metrics.export.date"),
      t("admin.metrics.export.users"),
      t("admin.metrics.export.documents"),
    ];
    const rows = timeSeriesData.map((item) => [
      item.date,
      item.users.toString(),
      item.documents.toString(),
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");

    // Download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `metrics-${period}-${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={refetch}
          className="px-4 py-2 bg-tiber-500 text-white rounded-lg hover:bg-tiber-500/90 transition-colors"
        >
          {t("common.retry")}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-tiber-900">
            {t("admin.metrics.title")}
          </h1>
          <p className="text-tiber-600/60 mt-1">
            {t("admin.metrics.subtitle")}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleExport}
            disabled={timeSeriesData.length === 0}
            className="flex items-center space-x-2 px-4 py-2 bg-tiber-500 text-white rounded-lg hover:bg-tiber-500/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
            <span>{t("admin.metrics.export.button")}</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-lg border border-romance-200 p-6"
        >
          <div className="flex items-center space-x-3 mb-2">
            <ChartBarIcon className="w-6 h-6 text-tiber-500" />
            <h3 className="text-sm font-medium text-tiber-600/60">
              {t("admin.metrics.summary.totalUsers")}
            </h3>
          </div>
          <p className="text-3xl font-bold text-tiber-900">
            {stats?.totalUsers || 0}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-lg border border-romance-200 p-6"
        >
          <div className="flex items-center space-x-3 mb-2">
            <ChartBarIcon className="w-6 h-6 text-atlantis-600" />
            <h3 className="text-sm font-medium text-tiber-600/60">
              {t("admin.metrics.summary.activeUsers")}
            </h3>
          </div>
          <p className="text-3xl font-bold text-tiber-900">
            {stats?.activeUsers || 0}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-lg border border-romance-200 p-6"
        >
          <div className="flex items-center space-x-3 mb-2">
            <ChartBarIcon className="w-6 h-6 text-tiber-600" />
            <h3 className="text-sm font-medium text-tiber-600/60">
              {t("admin.metrics.summary.totalDocuments")}
            </h3>
          </div>
          <p className="text-3xl font-bold text-tiber-900">
            {stats?.totalDocuments || 0}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-lg border border-romance-200 p-6"
        >
          <div className="flex items-center space-x-3 mb-2">
            <ChartBarIcon className="w-6 h-6 text-yellow-600" />
            <h3 className="text-sm font-medium text-tiber-600/60">
              {t("admin.metrics.summary.documentsToday")}
            </h3>
          </div>
          <p className="text-3xl font-bold text-tiber-900">
            {stats?.documentsToday || 0}
          </p>
        </motion.div>
      </div>

      {/* Period Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border border-romance-200 p-6"
      >
        <div className="flex items-center space-x-4">
          <CalendarIcon className="w-5 h-5 text-tiber-600/60" />
          <Select
            value={period}
            label={t("admin.metrics.period")}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setPeriod(e.target.value as Period)
            }
            options={[
              { value: "day", label: t("admin.metrics.periods.day") },
              { value: "week", label: t("admin.metrics.periods.week") },
              { value: "month", label: t("admin.metrics.periods.month") },
              { value: "year", label: t("admin.metrics.periods.year") },
            ]}
          />
        </div>
      </motion.div>

      {/* Charts */}
      {loadingTimeSeries ? (
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500"></div>
        </div>
      ) : (
        <MetricsCharts
          userStats={
            stats
              ? {
                  usersByType: stats.usersByType,
                  usersByCountry: stats.usersByCountry,
                }
              : undefined
          }
          documentStats={documentStats || undefined}
          timeSeriesData={timeSeriesData}
        />
      )}
    </div>
  );
};
